// SPDX-License-Identifier: AGPL-3.0-or-later

import {StatusSlate} from '@app/features/app/components/dialogs/shared/StatusSlate';
import Channels from '@app/features/channel/state/Channels';
import styles from '@app/features/guild/components/modals/guild_tabs/GuildWebhooksTab.module.css';
import Permission from '@app/features/permissions/state/Permission';
import {Spinner} from '@app/features/ui/components/Spinner';
import * as WebhookCommands from '@app/features/webhook/commands/WebhookCommands';
import {WebhookListItem} from '@app/features/webhook/components/WebhookListItem';
import {useWebhookUpdates} from '@app/features/webhook/hooks/useWebhookUpdates';
import type {Webhook} from '@app/features/webhook/models/Webhook';
import Webhooks from '@app/features/webhook/state/Webhooks';
import {Permissions, TEXT_BASED_CHANNEL_TYPES} from '@app/vendor/constants/ChannelConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {RobotIcon, WarningCircleIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';

const GUILD_WEBHOOKS_TAB_ID = 'webhooks';
const GuildWebhooksTab: React.FC<{guildId: string}> = observer(({guildId}) => {
	const {t} = useLingui();
	const canManageWebhooks = Permission.can(Permissions.MANAGE_WEBHOOKS, {guildId});
	const fetchStatus = Webhooks.getGuildFetchStatus(guildId);
	const webhooks = Webhooks.getGuildWebhooks(guildId);
	const guildChannels = Channels.getGuildChannels(guildId);
	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
	const setExpanded = useCallback((id: string, expanded: boolean) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (expanded) next.add(id);
			else next.delete(id);
			return next;
		});
	}, []);
	const availableChannels = useMemo(
		() =>
			guildChannels
				.filter((ch) => TEXT_BASED_CHANNEL_TYPES.has(ch.type))
				.map((ch) => ({id: ch.id, label: ch.name ?? t`Unknown Channel`})),
		[guildChannels],
	);
	const channelNameMap = useMemo(() => {
		const map = new Map<string, string>();
		for (const ch of guildChannels) map.set(ch.id, ch.name ?? t`Unknown Channel`);
		return map;
	}, [guildChannels]);
	const sortedWebhooks = useMemo(() => {
		return [...webhooks].sort((a, b) => {
			const channelA = channelNameMap.get(a.channelId) ?? '';
			const channelB = channelNameMap.get(b.channelId) ?? '';
			if (channelA.localeCompare(channelB) !== 0) {
				return channelA.localeCompare(channelB, undefined, {numeric: true, sensitivity: 'base'});
			}
			return a.name.localeCompare(b.name, undefined, {numeric: true, sensitivity: 'base'});
		});
	}, [webhooks, channelNameMap]);
	useEffect(() => {
		if (!canManageWebhooks) return;
		if (fetchStatus === 'idle') {
			void WebhookCommands.fetchGuildWebhooks?.(guildId);
		}
	}, [fetchStatus, guildId, canManageWebhooks]);
	const {handleUpdate, formVersion} = useWebhookUpdates({
		tabId: GUILD_WEBHOOKS_TAB_ID,
		canManage: canManageWebhooks,
		originals: webhooks,
	});
	const header = (
		<div className={styles.header}>
			<h2 className={styles.title}>
				<Trans>Webhooks</Trans>
			</h2>
			<p className={styles.subtitle}>
				<Trans>View and manage every webhook configured across your community.</Trans>
			</p>
		</div>
	);
	return (
		<div className={styles.container}>
			{header}
			{!canManageWebhooks && (
				<div className={styles.notice}>
					<Trans>You need the Manage Webhooks permission to view and edit webhooks for this community.</Trans>
				</div>
			)}
			{canManageWebhooks && (
				<div className={styles.infoBox}>
					<Trans>
						To create a webhook, open the channel's settings and use the <strong>Webhooks</strong> tab. You can still
						edit and organize all existing webhooks here.
					</Trans>
				</div>
			)}
			{fetchStatus === 'pending' && (
				<div className={styles.spinnerContainer}>
					<Spinner />
				</div>
			)}
			{fetchStatus === 'error' && (
				<StatusSlate
					Icon={WarningCircleIcon}
					title={t`Failed to Load Webhooks`}
					description={t`There was an error loading the webhooks. Please try again.`}
					actions={[
						{
							text: t`Retry`,
							onClick: () => WebhookCommands.fetchGuildWebhooks?.(guildId),
							variant: 'primary',
						},
					]}
					fullHeight={true}
				/>
			)}
			{fetchStatus === 'success' && sortedWebhooks.length > 0 && (
				<div className={styles.webhookList}>
					{sortedWebhooks.map((webhook: Webhook) => (
						<WebhookListItem
							key={webhook.id}
							webhook={webhook}
							channelName={channelNameMap.get(webhook.channelId) ?? undefined}
							onUpdate={handleUpdate}
							onDelete={(webhook) => WebhookCommands.deleteWebhook(webhook.id)}
							availableChannels={availableChannels}
							defaultExpanded={false}
							isExpanded={expandedIds.has(webhook.id)}
							onExpandedChange={(open) => setExpanded(webhook.id, open)}
							formVersion={formVersion}
						/>
					))}
				</div>
			)}
			{fetchStatus === 'success' && sortedWebhooks.length === 0 && (
				<StatusSlate
					Icon={RobotIcon}
					title={<Trans>No webhooks</Trans>}
					description={
						<Trans>
							This community doesn't have any webhooks yet. Go to a channel's settings and use the Webhooks tab to
							create one.
						</Trans>
					}
					fullHeight={true}
				/>
			)}
		</div>
	);
});

export default GuildWebhooksTab;
